import React, { useState } from 'react';
import { Plus, X, Trash2, AlertCircle } from 'lucide-react';
import { BASE_URL } from 'utils/constant';


export default function MultipleVendorsInvite({ 
  rfq , 
  vendors, 
  onClose,
  onSuccess,
  addAlert
}) {
  const [globalValidityDays, setGlobalValidityDays] = useState(7);
  const [vendorForms, setVendorForms] = useState([
    {
      id: Date.now(),
      vendor_whatsapp_number: '',
      vendor_name: '',
      vendor_address: '',
      isExistingVendor: false,
      selectedVendor: null,
      error: ''
    }
  ]);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitProgress, setSubmitProgress] = useState({ current: 0, total: 0 });

  const addVendorForm = () => {
    setVendorForms([
      ...vendorForms,
      {
        id: Date.now(),
        vendor_whatsapp_number: '',
        vendor_name: '',
        vendor_address: '',
        isExistingVendor: false,
        selectedVendor: null,
        error: ''
      }
    ]);
  };

  const removeVendorForm = (id) => {
    if (vendorForms.length > 1) {
      setVendorForms(vendorForms.filter(form => form.id !== id));
    }
  };

  const updateVendorForm = (id, field, value) => {
    setVendorForms(vendorForms.map(form => {
      if (form.id === id) {
        const updatedForm = { ...form, [field]: value, error: '' };
        
        // Check for existing vendor when phone number changes
        if (field === 'vendor_whatsapp_number' && value && vendors.length > 0) {
          const normalizedInput = value.startsWith('+91') ? value : `+91${value.replace(/^\+91/, '')}`;
          const matchedVendor = vendors.find(v => {
            const vendorNumber = v.contact_no?.trim();
            return vendorNumber === normalizedInput || vendorNumber === normalizedInput.replace(/^\+91/, '');
          });

          if (matchedVendor) {
            updatedForm.isExistingVendor = true;
            updatedForm.selectedVendor = matchedVendor;
            updatedForm.vendor_name = matchedVendor.name || '';
            updatedForm.vendor_address = matchedVendor.address || '';
          } else {
            updatedForm.isExistingVendor = false;
            updatedForm.selectedVendor = null;
          }
        }
        
        return updatedForm;
      }
      return form;
    }));
  };

  const selectExistingVendor = (formId, vendorId) => {
    if (!vendorId) {
      updateVendorForm(formId, 'selectedVendor', null);
      updateVendorForm(formId, 'isExistingVendor', false);
      updateVendorForm(formId, 'vendor_whatsapp_number', '');
      updateVendorForm(formId, 'vendor_name', '');
      updateVendorForm(formId, 'vendor_address', '');
      return;
    }

    const vendor = vendors.find(v => v.id === parseInt(vendorId));
    if (vendor) {
      // Check if this vendor's phone number is already added
      const phoneNum = vendor.contact_no.replace(/^\+91/, '');
      const isDuplicate = vendorForms.some(f => f.id !== formId && f.vendor_whatsapp_number === phoneNum);
      
      if (isDuplicate) {
        addAlert('This vendor is already added to the list', 'error');
        return;
      }

      setVendorForms(vendorForms.map(form => {
        if (form.id === formId) {
          return {
            ...form,
            selectedVendor: vendor,
            isExistingVendor: true,
            vendor_whatsapp_number: vendor.contact_no.replace(/^\+91/, ''),
            vendor_name: vendor.name || '',
            vendor_address: vendor.address || '',
            error: ''
          };
        }
        return form;
      }));
    }
  };

  const validateForms = () => {
    let isValid = true;
    const updatedForms = vendorForms.map(form => {
      if (!form.vendor_whatsapp_number || !form.vendor_name) {
        return { ...form, error: 'Phone number and name are required' };
      }
      if (form.vendor_whatsapp_number.length !== 10) {
        return { ...form, error: 'Phone number must be 10 digits' };
      }
      return form;
    });

    setVendorForms(updatedForms);
    isValid = updatedForms.every(form => !form.error);

    // Check for duplicate phone numbers
    const phoneNumbers = updatedForms.map(f => f.vendor_whatsapp_number);
    const duplicates = phoneNumbers.filter((num, idx) => phoneNumbers.indexOf(num) !== idx);
    
    if (duplicates.length > 0) {
      addAlert('Duplicate phone numbers found. Please remove duplicates.', 'error');
      isValid = false;
    }

    return isValid;
  };

  const handlePhoneChange = (formId, value) => {
    const cleanValue = value.replace(/[^\d]/g, '').slice(0, 10);
    
    // Check for duplicates in real-time
    if (cleanValue.length === 10) {
      const isDuplicate = vendorForms.some(f => f.id !== formId && f.vendor_whatsapp_number === cleanValue);
      if (isDuplicate) {
        setVendorForms(vendorForms.map(form => {
          if (form.id === formId) {
            return { ...form, vendor_whatsapp_number: cleanValue, error: 'This phone number is already added' };
          }
          return form;
        }));
        return;
      }
    }
    
    updateVendorForm(formId, 'vendor_whatsapp_number', cleanValue);
  };

  const handleSubmitAll = async () => {
    if (!validateForms()) {
      return;
    }

    setIsSubmitting(true);
    setSubmitProgress({ current: 0, total: vendorForms.length });
    
    let successCount = 0;
    let failCount = 0;
    const errors = [];

    for (let i = 0; i < vendorForms.length; i++) {
      const form = vendorForms[i];
      setSubmitProgress({ current: i + 1, total: vendorForms.length });

      try {
        const payload = {
          rfq: rfq.id,
          vendor_whatsapp_number: `+91${form.vendor_whatsapp_number}`,
          validity_days: globalValidityDays,
          vendor_name: form.vendor_name,
        };

        if (form.vendor_address) {
          payload.vendor_address = form.vendor_address;
        }

        const res = await fetch(`${BASE_URL}/inventory/rfq-invitations/create/`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${localStorage.getItem('access')}`,
          },
          body: JSON.stringify(payload),
        });

        const data = await res.json();

        if (data.success) {
          successCount++;
        } else {
          failCount++;
          errors.push(`${form.vendor_name}: ${data.error || 'Failed to send'}`);
        }
      } catch (err) {
        failCount++;
        errors.push(`${form.vendor_name}: Network error`);
      }

      // Small delay between requests to avoid rate limiting
      if (i < vendorForms.length - 1) {
        await new Promise(resolve => setTimeout(resolve, 300));
      }
    }

    setIsSubmitting(false);
    setSubmitProgress({ current: 0, total: 0 });

    // Show results
    if (successCount > 0) {
      addAlert(`Successfully sent ${successCount} invitation(s)`, 'success');
    }
    if (failCount > 0) {
      addAlert(`Failed to send ${failCount} invitation(s)`, 'error');
      if (errors.length > 0) {
        console.error('Invitation errors:', errors);
      }
    }

    if (successCount > 0) {
      onSuccess();
      onClose();
    }
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg shadow-xl w-[60vw] h-[75vh] flex flex-col">
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-gray-200">
          <div>
            <h3 className="text-lg font-semibold text-gray-900">
              Send Invitation to Multiple Vendors
            </h3>
            <p className="text-sm text-gray-500 mt-1">
              {rfq?.rfq_number}
            </p>
          </div>
          <button
            onClick={onClose}
            disabled={isSubmitting}
            className="w-8 h-8 rounded-full hover:bg-gray-100 flex items-center justify-center text-gray-600 disabled:opacity-50"
          >
            <X size={20} />
          </button>
        </div>

        {/* Global Link Validity */}
        <div className="px-6 py-2 bg-purple-50 border-b border-purple-100">
          <div className="max-w-xs">
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Link Validity (Days) - Applies to All Vendors
            </label>
            <input
              type="number"
              min="1"
              value={globalValidityDays}
              onChange={(e) => setGlobalValidityDays(parseInt(e.target.value) || 7)}
              disabled={isSubmitting}
              className="w-full px-3 py-2 border border-purple-300 rounded-md focus:outline-none focus:ring-2 focus:ring-[#3B0069] disabled:opacity-50"
            />
          </div>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto px-6 py-4">
          <div className="space-y-4">
            {vendorForms.map((form, index) => (
              <div
                key={form.id}
                className="border border-gray-200 rounded-lg p-4 bg-gray-50"
              >
                <div className="flex items-center justify-between mb-4">
                  <h4 className="text-sm font-semibold text-gray-700">
                    Vendor {index + 1}
                  </h4>
                  {vendorForms.length > 1 && (
                    <button
                      onClick={() => removeVendorForm(form.id)}
                      disabled={isSubmitting}
                      className="text-red-600 hover:text-red-700 disabled:opacity-50"
                    >
                      <Trash2 size={18} />
                    </button>
                  )}
                </div>

                {form.error && (
                  <div className="mb-3 p-2 bg-red-50 border border-red-200 rounded-md flex items-start gap-2">
                    <AlertCircle size={16} className="text-red-600 mt-0.5 flex-shrink-0" />
                    <p className="text-sm text-red-600">{form.error}</p>
                  </div>
                )}

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  {/* Select Existing Vendor */}
                  <div className="md:col-span-2">
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Select Existing Vendor (Optional)
                    </label>
                    <select
                      value={form.selectedVendor?.id || ''}
                      onChange={(e) => selectExistingVendor(form.id, e.target.value)}
                      disabled={isSubmitting}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-[#3B0069] disabled:opacity-50"
                    >
                      <option value="">-- Select vendor or enter manually --</option>
                      {vendors.map((v) => (
                        <option key={v.id} value={v.id}>
                          {v.name} ({v.contact_no})
                        </option>
                      ))}
                    </select>
                  </div>

                  {/* WhatsApp Number */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      WhatsApp Number <span className="text-red-500">*</span>
                    </label>
                    <div className="flex items-center gap-2">
                      <div className="px-3 py-2 bg-gray-100 border border-gray-300 rounded-md text-gray-700">
                        +91
                      </div>
                      <input
                        type="text"
                        placeholder="10-digit number"
                        value={form.vendor_whatsapp_number}
                        onChange={(e) => handlePhoneChange(form.id, e.target.value)}
                        disabled={form.isExistingVendor || isSubmitting}
                        className={`flex-1 px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-[#3B0069] ${
                          form.isExistingVendor
                            ? 'bg-gray-100 text-gray-500 cursor-not-allowed'
                            : 'border-gray-300'
                        }`}
                      />
                    </div>
                    {form.isExistingVendor && (
                      <p className="text-xs text-green-600 mt-1">
                        Matched: {form.selectedVendor?.name}
                      </p>
                    )}
                  </div>

                  {/* Vendor Name */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Vendor Name <span className="text-red-500">*</span>
                    </label>
                    <input
                      type="text"
                      placeholder="Enter vendor name"
                      value={form.vendor_name}
                      onChange={(e) =>
                        updateVendorForm(form.id, 'vendor_name', e.target.value)
                      }
                      disabled={form.isExistingVendor || isSubmitting}
                      className={`w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-[#3B0069] ${
                        form.isExistingVendor
                          ? 'bg-gray-100 text-gray-500 cursor-not-allowed'
                          : 'border-gray-300'
                      }`}
                    />
                  </div>

                  {/* Vendor Address */}
                  <div className="md:col-span-2">
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Vendor Address (Optional)
                    </label>
                    <textarea
                      placeholder="Enter vendor address"
                      value={form.vendor_address}
                      onChange={(e) =>
                        updateVendorForm(form.id, 'vendor_address', e.target.value)
                      }
                      disabled={form.isExistingVendor || isSubmitting}
                      rows={2}
                      className={`w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-[#3B0069] ${
                        form.isExistingVendor
                          ? 'bg-gray-100 text-gray-500 cursor-not-allowed'
                          : 'border-gray-300'
                      }`}
                    />
                  </div>
                </div>
              </div>
            ))}
          </div>

          {/* Add Vendor Button */}
          <button
            onClick={addVendorForm}
            disabled={isSubmitting}
            className="mt-4 w-full px-4 py-2 border-2 border-dashed border-gray-300 rounded-lg text-gray-600 hover:border-[#3B0069] hover:text-[#3B0069] flex items-center justify-center gap-2 disabled:opacity-50"
          >
            <Plus size={18} />
            Add Another Vendor
          </button>
        </div>

        {/* Footer */}
        <div className="px-6 py-4 border-t rounded-b-lg border-gray-200 bg-gray-50">
          {isSubmitting && (
            <div className="mb-3">
              <div className="flex items-center justify-between text-sm text-gray-600 mb-1">
                <span>Sending invitations...</span>
                <span>{submitProgress.current} of {submitProgress.total}</span>
              </div>
              <div className="w-full bg-gray-200 rounded-full h-2">
                <div
                  className="bg-[#3B0069] h-2 rounded-full transition-all duration-300"
                  style={{
                    width: `${(submitProgress.current / submitProgress.total) * 100}%`
                  }}
                />
              </div>
            </div>
          )}

          <div className="flex gap-3">
            <button
              onClick={onClose}
              disabled={isSubmitting}
              className="flex-1 px-4 py-2 text-sm border border-gray-300 rounded-md text-gray-700 hover:bg-gray-50 disabled:opacity-50"
            >
              Cancel
            </button>
            <button
              onClick={handleSubmitAll}
              disabled={isSubmitting || vendorForms.length === 0}
              className="flex-1 px-4 py-2 text-sm bg-[#3B0069] text-white rounded-md hover:bg-[#2d0051] disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {isSubmitting
                ? `Sending ${submitProgress.current}/${submitProgress.total}...`
                : `Send ${vendorForms.length} Invitation${vendorForms.length !== 1 ? 's' : ''}`}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}